/**
 * The assistant conversation, held above the support panel that shows it.
 *
 * The panel shows the chat only while its "Ask Artemis" tab is open, but the
 * conversation outlives that: switching tabs or closing the bubble keeps it,
 * a running turn keeps being polled, and a turn waiting for pictures keeps its
 * hidden frame (assistant-view-runner.tsx) mounted, or it would never resume.
 */
import {
	createContext,
	type Dispatch,
	type ReactNode,
	type SetStateAction,
	useContext,
	useEffect,
	useState,
} from "react";
import {
	type AssistantConversation,
	getAssistantConversation,
} from "../../api/assistant/assistant";
import { AssistantViewRunner } from "./assistant-view-runner";

const POLL_MS = 1500;

interface AssistantState {
	conversation: AssistantConversation | null;
	setConversation: Dispatch<SetStateAction<AssistantConversation | null>>;
	error: string | null;
	setError: Dispatch<SetStateAction<string | null>>;
}

const AssistantContext = createContext<AssistantState | null>(null);

export const AssistantProvider = ({ children }: { children: ReactNode }) => {
	const [conversation, setConversation] = useState<AssistantConversation | null>(
		null
	);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		if (!conversation?.running) {
			return;
		}
		const timer = window.setTimeout(() => {
			getAssistantConversation(conversation.id)
				.then(setConversation)
				.catch((err: Error) => setError(err.message));
		}, POLL_MS);
		return () => window.clearTimeout(timer);
	}, [conversation]);

	return (
		<AssistantContext.Provider
			value={{ conversation, setConversation, error, setError }}
		>
			{children}
			{conversation?.awaiting_view && (
				<AssistantViewRunner
					conversation={conversation}
					onUpdate={setConversation}
					onError={setError}
				/>
			)}
		</AssistantContext.Provider>
	);
};

export const useAssistant = (): AssistantState => {
	const state = useContext(AssistantContext);
	if (state === null) {
		throw new Error("useAssistant needs an AssistantProvider above it");
	}
	return state;
};
